import { saveAs } from "file-saver";
import { PROMO_STAT_TILES, SEVERITY, STAT_TILE_FILTER } from "./constants";

/**
 * Format a data object with a name property.
 * Get the name, or if unable return the original value.
 */
export const nameValueFormatter = ({ value }) => value?.name || value;

/** Compare two stages by their position values (null sorts last) */
export const stageComparator = (stageA, stageB) => {
  const posA = stageA?.position ?? Infinity;
  const posB = stageB?.position ?? Infinity;
  return posA - posB;
};

/** Compare two objects by their name property alphabetically (null sorts last) */
export const nameComparator = (objA, objB) => {
  const nameA = objA?.name ?? "";
  const nameB = objB?.name ?? "";
  if (!nameA && !nameB) return 0;
  if (!nameA) return 1;
  if (!nameB) return -1;
  return nameA.localeCompare(nameB);
};

/**
 * Map severity level to Badge color
 * @param {string} severity - One of SEVERITY values
 * @returns {string} Badge color
 */
export const getSeverityColor = (severity) => {
  const colorMap = {
    [SEVERITY.OK]: "primary",
    [SEVERITY.INACTIVE]: "base",
    [SEVERITY.ERROR]: "error",
    [SEVERITY.WARNING]: "warning",
    [SEVERITY.INFO]: "base",
  };
  return colorMap[severity] || "base";
};

/**
 * Map severity level to icon name
 * @param {string} severity - One of SEVERITY values
 * @returns {string} Font Awesome icon name
 */
export const getSeverityIcon = (severity) => {
  const iconMap = {
    [SEVERITY.OK]: "fa-circle-check",
    [SEVERITY.INACTIVE]: "fa-ban",
    [SEVERITY.ERROR]: "fa-circle-xmark",
    [SEVERITY.WARNING]: "fa-triangle-exclamation",
    [SEVERITY.INFO]: "fa-circle-info",
  };
  return iconMap[severity] || "fa-circle-info";
};

/**
 * Bucket a promotion summary into the stat tile it belongs under, or null when
 * it belongs under none (INFO / INACTIVE carry no tile).
 *
 * Single source of truth for the promotion list: both the tile counts and the
 * grid's external filter route through here, so a promo can never be counted in
 * one bucket but filtered into another.
 *
 * @param {Object} promo - PromotionSummaryResponse row
 * @returns {string|null} A STAT_TILE_FILTER value, or null if unbucketed
 */
export const getPromoTileBucket = (promo) => {
  const severity = promo?.maxSeverity;
  if (severity === SEVERITY.ERROR) return STAT_TILE_FILTER.ERROR;
  if (severity === SEVERITY.WARNING) return STAT_TILE_FILTER.WARNING;
  if (severity === SEVERITY.OK || (promo?.issueCount ?? 0) === 0) {
    return STAT_TILE_FILTER.OK;
  }
  return null;
};

/**
 * Predicate for the promotion grid's external filter. A null/undefined filter
 * means no tile is selected and every row passes.
 */
export const promoMatchesTileFilter = (promo, filter) =>
  !filter || getPromoTileBucket(promo) === filter;

/**
 * True when a value is a tile the promotion list can actually filter by.
 *
 * Vets the `tile` URL param, which is user-editable and arrives as a bare
 * string. A value no promo buckets into would filter the grid down to nothing
 * with no tile lit up — a page that looks broken rather than a link that was
 * wrong — so this backs the tiles the list renders rather than every key in
 * STAT_TILE_FILTER. Case-sensitive on purpose: these strings are the Redux
 * filter values and the count keys too, so accepting a variant would break tile
 * highlighting and counts.
 *
 * @param {*} value - Candidate tile key
 * @returns {boolean}
 */
export const isPromoTileFilter = (value) =>
  typeof value === "string" && PROMO_STAT_TILES.includes(value);

/**
 * Extract filename from Content-Disposition header.
 * Handles both RFC 2231 and RFC 6266 formats.
 *
 * @param {string} contentDisposition - Content-Disposition header value
 * @returns {string|null} Extracted filename or null if not found
 */
const extractFilenameFromHeader = (contentDisposition) => {
  if (!contentDisposition) return null;

  // Try RFC 6266 filename*=UTF-8''encoded-filename format first
  const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (filenameStarMatch) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  // Try standard filename="value" or filename=value format
  const filenameMatch = contentDisposition.match(
    /filename[^;=\n]*=\s*"?([^";\n]+)"?/i,
  );
  if (filenameMatch) {
    return filenameMatch[1].trim();
  }

  return null;
};

/**
 * Download a file from an authenticated API endpoint.
 * Uses file-saver library for robust cross-browser downloads.
 *
 * @param {Object} apiClient - Axios instance with auth configured
 * @param {string} url - API endpoint URL
 * @param {string} [defaultFilename="download"] - Fallback filename if Content-Disposition header is missing
 * @returns {Promise<void>}
 * @throws {Error} If the download fails
 */
export const downloadFile = async (
  apiClient,
  url,
  defaultFilename = "download",
) => {
  const response = await apiClient.get(url, { responseType: "blob" });

  // Extract filename from Content-Disposition header or use default
  const contentDisposition = response.headers["content-disposition"];
  const filename =
    extractFilenameFromHeader(contentDisposition) || defaultFilename;

  saveAs(response.data, filename);
};
